import { ApiResponse, ApiVehicle, Vehicle } from "../types";

const BASE_API_URL = "https://api.aptoscan.com/v1/collectionv2s/0x20e07ab7d5d02ccf286a11cbe3fa36df0f4bdec4578d769a6d8cc8a8365ca5a3/tokens";

// 마지막으로 가져온 페이지 번호를 저장
let lastFetchedPage = 0;
// 전체 페이지 수 (마지막 페이지 번호)
let totalPages = 0;

/**
 * AutoProof API에서 차량 NFT 데이터를 가져옵니다
 * @param fetchAll 모든 페이지의 데이터를 처음부터 가져올지 여부
 * @returns 가져온 차량 데이터 배열
 */
export async function fetchVehicles(fetchAll: boolean = false): Promise<Vehicle[]> {
  try {
    let allVehicles: ApiVehicle[] = [];
    
    // fetchAll이 true면 첫페이지부터, 아니면 마지막으로 가져온 페이지부터 시작
    let currentPage = fetchAll ? 1 : lastFetchedPage > 0 ? lastFetchedPage : 1;
    let hasMoreData = true;
    
    console.log(`데이터 가져오기 시작: ${fetchAll ? '전체 데이터' : '새 데이터만'}, 시작 페이지: ${currentPage}`);
    
    // 데이터 가져오기
    while (hasMoreData) {
      const apiUrl = `${BASE_API_URL}?page=${currentPage}&cluster=mainnet`;
      console.log(`페이지 ${currentPage} 데이터 요청 중...`);
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`페이지 ${currentPage} 네트워크 응답이 올바르지 않습니다`);
      }
      
      const data: ApiResponse = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error(`페이지 ${currentPage} 데이터 형식이 올바르지 않습니다`);
      }
      
      // 데이터 추가
      allVehicles = [...allVehicles, ...data.data];
      
      // 페이지당 20개씩 있으므로, 20개 미만이면 마지막 페이지
      if (data.data.length < 20) {
        hasMoreData = false;
        totalPages = currentPage; // 마지막 페이지 번호 저장
        console.log(`마지막 페이지(${currentPage}) 도달. 총 페이지 수: ${totalPages}`);
      } else {
        // 현재 페이지 번호 저장 (나중에 이 페이지부터 새 데이터 요청하기 위함)
        lastFetchedPage = currentPage;
        currentPage++;
      }
    }
    
    console.log(`총 ${allVehicles.length}개 차량 데이터 가져옴`);
    return parseVehiclesFromApi(allVehicles);
    
  } catch (error) {
    console.error('차량 데이터를 가져오는데 실패했습니다:', error);
    return [];
  }
}

/**
 * 새로운 차량 데이터만 가져옵니다 (마지막으로 가져온 페이지부터)
 * 첫 호출 또는 데이터가 없는 경우 전체 데이터를 가져옵니다
 */
export async function fetchNewVehicles(): Promise<Vehicle[]> {
  // 마지막으로 가져온 페이지가 없거나 0이면 전체 데이터 가져오기
  if (lastFetchedPage === 0) {
    return fetchVehicles(true);
  }
  
  // 마지막으로 가져온 페이지부터 새 데이터 가져오기
  return fetchVehicles(false);
}

/**
 * API 응답 데이터를 애플리케이션에서 사용하는 Vehicle 형식으로 변환합니다
 */
function parseVehiclesFromApi(apiVehicles: ApiVehicle[]): Vehicle[] {
  // metadata가 null인 경우에도 처리할 수 있도록 필터링 조건 완화
  const parsedVehicles = apiVehicles
    .filter(vehicle => vehicle.metadata && vehicle.metadata.carInfo)
    .map(vehicle => {
      const tokenNumber = vehicle.token_name.split('#')[1];
      
      // 이미지 URL에 해상도 파라미터 추가 (1024x1024)
      let imageUrl = vehicle.metadata.image;
      
      // 이미지 URL에 쿼리 파라미터 추가
      if (imageUrl.includes('?')) {
        imageUrl = `${imageUrl}&width=1024&height=1024`;
      } else {
        imageUrl = `${imageUrl}?width=1024&height=1024`;
      }
      
      return {
        id: vehicle.token_data_id,
        tokenName: vehicle.token_name,
        image: imageUrl,
        vehicleName: `${vehicle.metadata.carInfo.makerName || ''} ${vehicle.metadata.carInfo.model}`.trim(),
        price: parseInt(vehicle.metadata.carInfo.price, 10),
        year: vehicle.metadata.carInfo.year || '',
        registeredAt: formatTimestamp(vehicle.last_transaction_timestamp),
        grade: vehicle.metadata.carInfo.grade,
        fuel: vehicle.metadata.carInfo.fuel,
        color: vehicle.metadata.carInfo.color,
        makerName: vehicle.metadata.carInfo.makerName
      };
    });
  
  // 최신 등록순으로 정렬 (timestamp 기준 내림차순)
  return parsedVehicles.sort((a, b) => {
    return new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime();
  });
}

/**
 * Timestamp 문자열을 ISO 형식으로 변환합니다
 * API는 "1743060572000000" 형식의 timestamp를 반환합니다
 */
function formatTimestamp(timestamp: string): string {
  // 마이크로초를 밀리초로 변환 (마지막 3자리 제거)
  const milliseconds = parseInt(timestamp.slice(0, -3), 10);
  return new Date(milliseconds).toISOString();
} 